<template>
  <div class="ed-dash">
    <!-- ── Indicadores principales ── -->
    <section class="ed-dash__section">
      <header class="ed-dash__h">
        <span class="ed-dash__h-num">A1</span>
        <h3 class="ed-dash__h-title">Indicadores principales</h3>
        <span class="ed-dash__h-rule"></span>
        <span class="ed-dash__h-meta">{{ periodLabel }}</span>
      </header>

      <div v-if="!loadingIndicadores" class="ed-dash__kpi-grid">
        <article v-for="item in indicadores" :key="item.id" class="ed-card ed-kpi-mini">
          <div class="ed-kpi-mini__icon" :style="{ background: item.color + '14', color: item.color }">
            <v-icon :color="item.color" size="22">{{ item.icon }}</v-icon>
          </div>
          <div class="ed-kpi-mini__body">
            <span class="ed-kpi-mini__label">{{ item.title }}</span>
            <span class="ed-kpi-mini__value">{{ item.amount }}</span>
          </div>
          <span class="ed-kpi-mini__rail" :style="{ background: item.color }"></span>
        </article>
      </div>
      <div v-else class="ed-dash__kpi-grid">
        <div v-for="n in 4" :key="'sk' + n" class="ed-card ed-kpi-mini ed-skeleton"></div>
      </div>
    </section>

    <!-- ── Ingresos vs Gastos (gráfico principal) ── -->
    <section class="ed-dash__section">
      <header class="ed-dash__h">
        <span class="ed-dash__h-num">A2</span>
        <h3 class="ed-dash__h-title">Ingresos vs Gastos</h3>
        <span class="ed-dash__h-rule"></span>
        <span class="ed-dash__h-meta">Mensual · acumulado</span>
      </header>

      <div class="ed-card ed-card--lg" v-if="!loadingCompras">
        <div class="ed-card__head">
          <div>
            <p class="ed-card__eyebrow">Balance mensual</p>
            <h4 class="ed-card__title">Comparativa de flujo</h4>
          </div>
          <div class="ed-legend">
            <span class="ed-legend__chip ed-legend__chip--sage"></span>
            Ingresos
            <span class="ed-legend__chip ed-legend__chip--claret"></span>
            Gastos
          </div>
        </div>
        <apexchart height="380" type="bar" :options="optionsInOut" :series="seriesInOut"></apexchart>
      </div>
      <div v-else class="ed-card ed-card--lg ed-skeleton" style="height: 420px;"></div>
    </section>

    <!-- ── Ventas: histórico + por ubicación ── -->
    <section class="ed-dash__section" v-if="!loadingSales && ventas.series.length > 0">
      <header class="ed-dash__h">
        <span class="ed-dash__h-num">A3</span>
        <h3 class="ed-dash__h-title">Ingresos · análisis</h3>
        <span class="ed-dash__h-rule"></span>
      </header>

      <div class="ed-grid" :class="{ 'ed-grid--cols-2': filters.location == 0, 'ed-grid--single': filters.location != 0 }">
        <div class="ed-card">
          <div class="ed-card__head">
            <div>
              <p class="ed-card__eyebrow">Histórico</p>
              <h4 class="ed-card__title">Ingresos por mes</h4>
            </div>
          </div>
          <BarChart :title_header="''" :data="ventas" :colors="false" />
        </div>

        <div v-if="filters.location == 0" class="ed-card">
          <div class="ed-card__head">
            <div>
              <p class="ed-card__eyebrow">Distribución</p>
              <h4 class="ed-card__title">Por ubicación</h4>
            </div>
          </div>
          <PieChart :title_header="''" :data="ventas_location" />
        </div>
      </div>
    </section>

    <!-- ── Ventas por hora + tendencia diaria ── -->
    <section class="ed-dash__section"
             v-if="(ventas_horas.series && ventas_horas.series.length) || (ventas_diarias.series && ventas_diarias.series.length)">
      <header class="ed-dash__h">
        <span class="ed-dash__h-num">A4</span>
        <h3 class="ed-dash__h-title">Movimiento</h3>
        <span class="ed-dash__h-rule"></span>
        <span class="ed-dash__h-meta">Tendencias recientes</span>
      </header>

      <div class="ed-grid ed-grid--cols-2">
        <div class="ed-card" v-if="ventas_horas.series && ventas_horas.series.length > 0">
          <div class="ed-card__head">
            <p class="ed-card__eyebrow">24 horas</p>
            <h4 class="ed-card__title">Ventas por hora</h4>
          </div>
          <LineChart :title_header="''" :data="ventas_horas" />
        </div>
        <div class="ed-card" v-if="ventas_diarias.series && ventas_diarias.series.length > 0">
          <div class="ed-card__head">
            <p class="ed-card__eyebrow">Diario</p>
            <h4 class="ed-card__title">Tendencia</h4>
          </div>
          <LineChart :title_header="''" :data="ventas_diarias" />
        </div>
      </div>
    </section>

    <!-- ── Compras (histórico + ubicación) ── -->
    <section class="ed-dash__section" v-if="!loadingCompras">
      <header class="ed-dash__h">
        <span class="ed-dash__h-num">A5</span>
        <h3 class="ed-dash__h-title">Compras · análisis</h3>
        <span class="ed-dash__h-rule"></span>
      </header>

      <div class="ed-grid ed-grid--cols-2">
        <div class="ed-card">
          <div class="ed-card__head">
            <p class="ed-card__eyebrow">Histórico</p>
            <h4 class="ed-card__title">Compras / Gastos por mes</h4>
          </div>
          <BarChart :title_header="''" :data="compras" />
        </div>
        <div class="ed-card">
          <div class="ed-card__head">
            <p class="ed-card__eyebrow">Distribución</p>
            <h4 class="ed-card__title">Gastos por ubicación</h4>
          </div>
          <PieChart :title_header="''" :data="compra_location" />
        </div>
      </div>
    </section>

    <!-- ── Cuentas por cobrar / pagar ── -->
    <section class="ed-dash__section" v-if="!loading && !loadingSales">
      <header class="ed-dash__h">
        <span class="ed-dash__h-num">A6</span>
        <h3 class="ed-dash__h-title">Cuentas</h3>
        <span class="ed-dash__h-rule"></span>
        <span class="ed-dash__h-meta">Antigüedad de saldos</span>
      </header>

      <div class="ed-grid ed-grid--cols-2">
        <div><DashboardCxC :filter="filters" /></div>
        <div><DashboardCxP :filter="filters" /></div>
      </div>
    </section>
  </div>
</template>

<script>
import moment from "moment";
moment.locale("es");
import PieChart from "@/components/Charts/Pie.vue";
import DonutChart from "@/components/Charts/DonutChart.vue";
import BarChart from "@/components/Charts/Bar.vue";
import Multiline from "@/components/Charts/Multiline.vue";
import LineChart from "@/components/Charts/Line.vue";
import DashboardCxC from "@/components/Transactions/DashboardCxC.vue";
import DashboardCxP from "@/components/Transactions/DashboardCxP.vue";

// Material palette aligned with Home.vue (Vuetify theme)
const EDC = {
  ink:    "#2A3547",
  graph:  "#5A6A85",
  muted:  "#8A98AB",
  rule:   "#E5EAF2",
  sage:   "#00897B", // primary (teal)
  brass:  "#F57F17", // warning (amber)
  claret: "#E53935", // error (red)
};

export default {
  data: () => ({
    loadingIndicadores: false,
    loadingSales: false,
    loadingCompras: false,
    loading: false,
    indicadores: [],
    ventas_location: { series: [], label: [] },
    compra_location: { series: [], label: [] },
    ventas_diarias:  { series: [], label: [] },
    ventas_horas:    { series: [], label: [] },
    ventas:          { series: [], label: [] },
    compras:         { series: [], label: [] },
    optionsInOut: {
      chart: {
        id: "InOut",
        stacked: false,
        fontFamily: "Plus Jakarta Sans, sans-serif",
        toolbar: { show: false },
        animations: { enabled: true, easing: "easeinout", speed: 800, dynamicAnimation: { speed: 350 } },
      },
      colors: [EDC.sage, EDC.claret],
      plotOptions: {
        bar: { horizontal: false, columnWidth: "44%", borderRadius: 2, dataLabels: { position: "top" } },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          if (!val) return "";
          if (val >= 1e6) return (val / 1e6).toFixed(1) + "M";
          if (val >= 1e3) return (val / 1e3).toFixed(1) + "k";
          return val;
        },
        offsetY: -20,
        style: { fontSize: "10px", colors: [EDC.graph], fontWeight: 600, fontFamily: "Plus Jakarta Sans, sans-serif" },
      },
      stroke: { show: true, width: 2, colors: ["transparent"] },
      xaxis: {
        categories: [],
        labels: { style: { colors: EDC.graph, fontSize: "11px", fontWeight: 500, fontFamily: "Plus Jakarta Sans, sans-serif" } },
        axisBorder: { show: false }, axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          formatter: (val) => val.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }),
          style: { colors: EDC.graph, fontSize: "11px", fontWeight: 500, fontFamily: "Plus Jakarta Sans, sans-serif" },
        },
      },
      fill: { opacity: 1 },
      tooltip: {
        theme: "light",
        y: { formatter: (val) => val.toLocaleString("en-US", { style: "currency", currency: "USD" }) },
      },
      legend: { show: false },
      grid: {
        borderColor: EDC.rule, strokeDashArray: 3,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
    },
    seriesInOut: [
      { name: "Ingresos", data: [] },
      { name: "Gastos",   data: [] },
    ],
  }),
  components: { PieChart, DonutChart, BarChart, Multiline, LineChart, DashboardCxC, DashboardCxP },
  props: {
    filters: {
      type: Object,
      default: () => ({ business: null, location: null, department_id: null }),
    },
  },

  computed: {
    periodLabel() {
      const f = this.filters || {};
      if (f.from_date && f.to_date) {
        return moment(f.from_date).format("DD MMM").toUpperCase() + " — " + moment(f.to_date).format("DD MMM YYYY").toUpperCase();
      }
      return "Período actual";
    },
  },

  watch: {
    filters: {
      handler(v) { this.updateData(v); },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    fetchCached(endpoint, payload) {
      return this.$store.dispatch("dashboard/fetchCached", {
        endpoint, payload,
        fetcher: async () => {
          const r = await this.api_Post(endpoint, payload);
          return await r.json();
        },
      });
    },

    async loadIndicadores(filter) {
      this.loadingIndicadores = true;
      try {
        const base = { business: filter.business, location: filter.location, from_date: filter.from_date, to_date: filter.to_date };
        const [dataSale, dataShop, cxc, cxp] = await Promise.all([
          this.fetchCached("income/sales_and_shop",        { ...base, type: 2 }),
          this.fetchCached("income/sales_and_shop",        { ...base, type: 1 }),
          this.fetchCached("income/pending_balance_amount",{ ...base, type: 2 }),
          this.fetchCached("income/pending_balance_amount",{ ...base, type: 1 }),
        ]);

        const cc = this.$store.state.auth.bussines_units.find(b => b.value == filter.business)?.currency_code || "";
        const list = [];
        list.push({ id: 1, title: "Compras", amount: this.currencyFormat(dataShop?.[0]?.total ?? 0) + " " + cc, color: EDC.brass,  icon: "shopping_cart" });
        list.push({ id: 2, title: "Ventas",  amount: this.currencyFormat(dataSale?.[0]?.total ?? 0) + " " + cc, color: EDC.sage,   icon: "sell" });
        if (cxc?.length) list.push({ id: 3, title: "Cuentas por cobrar", amount: this.currencyFormat(cxc[0].total) + " " + cc, color: EDC.ink,    icon: "account_balance_wallet" });
        if (cxp?.length) list.push({ id: 4, title: "Cuentas por pagar",  amount: this.currencyFormat(cxp[0].total) + " " + cc, color: EDC.claret, icon: "attach_money" });
        this.indicadores = list;
      } finally {
        this.loadingIndicadores = false;
      }
    },

    async loadSales(filter) {
      this.loadingSales = true;
      try {
        const base = { business: filter.business, location: filter.location, from_date: filter.from_date, to_date: filter.to_date };
        const [ventasDays, ventasLocations, ventasMonth, ventasHoras] = await Promise.all([
          this.fetchCached("income/dashboard_daily",    { ...base, type: 2 }),
          this.fetchCached("income/dashboard_location", { ...base, invoice: 2 }),
          this.fetchCached("income/dashboard_montly",   { business: filter.business, from_date: filter.from_date, to_date: filter.to_date, invoice: 2 }),
          this.fetchCached("income/dashboard_hours",    { ...base, type: 2 }),
        ]);
        const collect = (arr, k1, k2) => {
          const l = [], s = [];
          for (const i of (arr || [])) { l.push(i[k1]); s.push(parseFloat(i[k2])); }
          return { label: l, series: s };
        };
        this.ventas         = collect(ventasMonth,     "month_year",        "total_sales");
        this.ventas_diarias = collect(ventasDays,      "day_year",          "total_sales");
        this.ventas_horas   = collect(ventasHoras,     "hour_format",       "total_sales");
        this.ventas_location= collect(ventasLocations, "description_short", "total_amount_sum");
      } finally {
        this.loadingSales = false;
      }
    },

    async loadCompras(filter) {
      this.loadingCompras = true;
      try {
        const base = { business: filter.business, location: filter.location, from_date: filter.from_date, to_date: filter.to_date };
        const [comprasLocations, comprasMonth, monthVs] = await Promise.all([
          this.fetchCached("income/dashboard_location",          { ...base, invoice: 1 }),
          this.fetchCached("income/dashboard_montly",            { business: filter.business, from_date: filter.from_date, to_date: filter.to_date, invoice: 1 }),
          this.fetchCached("income/dashboard_months_vs_last_moth", base),
        ]);
        const collect = (arr, k1, k2) => {
          const l = [], s = [];
          for (const i of (arr || [])) { l.push(i[k1]); s.push(parseFloat(i[k2])); }
          return { label: l, series: s };
        };
        this.compras         = collect(comprasMonth,     "month_year",        "total_sales");
        this.compra_location = collect(comprasLocations, "description_short", "total_amount_sum");

        const cats = [], ing = [], com = [];
        for (const m of (monthVs || [])) { cats.push(m.nombre_mes); ing.push(m.tipos.ingresos); com.push(m.tipos.compras); }
        this.optionsInOut = { ...this.optionsInOut, xaxis: { ...this.optionsInOut.xaxis, categories: cats } };
        this.seriesInOut = [
          { name: "Ingresos", data: ing },
          { name: "Gastos",   data: com },
        ];
      } finally {
        this.loadingCompras = false;
      }
    },

    async updateData(filter) {
      if (!filter || !filter.business) return;
      await this.loadIndicadores(filter);
      this.loading = true;
      await Promise.all([this.loadSales(filter), this.loadCompras(filter)]);
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
$ink:    #2A3547;
$muted:  #5A6A85;
$muted-soft: #8A98AB;
$rule:   #E5EAF2;
$bg:     #F7F9FC;
$card:   #FFFFFF;

$primary:      #00897B;
$primary-soft: #E0F2F1;
$info:         #2196F3;
$info-soft:    #E3F2FD;
$accent:       #5C6BC0;
$accent-soft:  #E8EAF6;
$warning:      #F57F17;
$warning-soft: #FFF3E0;
$error:        #E53935;
$success:      #2E7D32;

$shadow: rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 8px 18px -6px;

$font: 'Plus Jakarta Sans', sans-serif;

.ed-dash {
  display: flex; flex-direction: column;
  gap: 36px;
  padding: 12px 0 24px;
  font-family: $font; color: $ink;
}
.ed-dash__section { display: flex; flex-direction: column; gap: 14px; }

.ed-dash__h {
  display: flex; align-items: center; gap: 14px;
  padding-bottom: 4px;
}
.ed-dash__h-num {
  font-family: $font; font-size: 11px; font-weight: 700;
  color: $primary; letter-spacing: .14em; text-transform: uppercase;
}
.ed-dash__h-title {
  font-family: $font; font-weight: 700;
  font-size: 22px; letter-spacing: -.01em; color: $ink;
  margin: 0;
}
.ed-dash__h-rule { flex: 1; height: 1px; background: $rule; }
.ed-dash__h-meta {
  font-family: $font; font-size: 11px; font-weight: 500;
  color: $muted; letter-spacing: .14em; text-transform: uppercase;
}

// ── Cards ──
.ed-card {
  background: $card;
  border: 1px solid $rule;
  border-radius: 14px;
  box-shadow: $shadow;
  padding: 22px 22px 12px;
  position: relative;
}
.ed-card--lg { padding: 26px 26px 14px; }
.ed-card__head {
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 16px; margin-bottom: 10px;
}
.ed-card__eyebrow {
  font-family: $font; font-size: 10px; font-weight: 600;
  color: $muted; letter-spacing: .22em; text-transform: uppercase;
  margin: 0 0 4px;
}
.ed-card__title {
  font-family: $font; font-weight: 700; font-size: 18px; letter-spacing: -.01em;
  color: $ink; margin: 0;
}

.ed-legend {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: $font; font-size: 12px; color: $muted;
  letter-spacing: .04em;
}
.ed-legend__chip {
  display: inline-block; width: 10px; height: 10px; margin-right: 4px;
  margin-left: 10px; border-radius: 3px;
}
.ed-legend__chip:first-child { margin-left: 0; }
.ed-legend__chip--sage   { background: $primary; }
.ed-legend__chip--claret { background: $error; }

// ── KPI mini ──
.ed-dash__kpi-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0;
  border: 1px solid $rule; background: $card;
  border-radius: 14px; overflow: hidden;
  box-shadow: $shadow;
}
.ed-kpi-mini {
  display: grid; grid-template-columns: 48px 1fr;
  align-items: center; gap: 14px;
  padding: 18px 22px;
  border-right: 1px solid $rule;
  background: $card; border: 1px solid $rule;
  margin: 0; position: relative;
  overflow: hidden;
}
.ed-dash__kpi-grid .ed-kpi-mini { border: 0; border-right: 1px solid $rule; }
.ed-dash__kpi-grid .ed-kpi-mini:last-child { border-right: 0; }
.ed-kpi-mini__icon {
  width: 44px; height: 44px; border-radius: 10px;
  display: grid; place-items: center;
}
.ed-kpi-mini__body { display: flex; flex-direction: column; gap: 4px; }
.ed-kpi-mini__label {
  font-family: $font; font-size: 10px; font-weight: 600;
  letter-spacing: .22em; text-transform: uppercase; color: $muted;
}
.ed-kpi-mini__value {
  font-family: $font; font-weight: 800; font-size: 22px; line-height: 1.1;
  letter-spacing: -.02em; color: $ink;
}
.ed-kpi-mini__rail {
  position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
}

// ── Grid layout helpers ──
.ed-grid { display: grid; gap: 18px; }
.ed-grid--cols-2 { grid-template-columns: 1fr 1fr; }
.ed-grid--single { grid-template-columns: 1fr; }

@media (max-width: 880px) {
  .ed-grid--cols-2 { grid-template-columns: 1fr; }
  .ed-dash__kpi-grid { grid-template-columns: 1fr 1fr; }
  .ed-dash__kpi-grid .ed-kpi-mini:nth-child(2n) { border-right: 0; }
  .ed-dash__kpi-grid .ed-kpi-mini { border-bottom: 1px solid $rule; }
}

// ── Skeleton ──
.ed-skeleton {
  min-height: 110px;
  border-radius: 14px;
  background: linear-gradient(90deg, $bg 0%, #EEF1F6 50%, $bg 100%) !important;
  background-size: 200% 100% !important;
  animation: shimmer 1.4s linear infinite;
  border: 1px solid $rule;
}
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

// Strip the legacy "modern-card" appearance so charts inherit the dashboard card
::v-deep .modern-card {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
  border-radius: 0 !important;
}

::v-deep .apexcharts-title-text,
::v-deep .apexcharts-legend-text,
::v-deep .apexcharts-text {
  font-family: $font !important;
}
</style>
